import { DownOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Card, Spin, Alert, Button, Dropdown, Menu } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { getTrailBalance } from "./account.api";
import "./account.css";

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const buildTrialBalanceRows = (data) => {
  const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
  const rows = [];
  const debits = data?.debits || [];
  const credits = data?.credits || [];
  debits.forEach((r) => rows.push([r.subAccount || "—", fmt(r.balance), ""]));
  credits.forEach((r) => rows.push([r.subAccount || "—", "", fmt(r.balance)]));
  rows.push(["TOTAL", fmt(data?.totalDebit), fmt(data?.totalCredit)]);
  return rows;
};

const exportPDF = (data) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  doc.setFontSize(12);
  doc.text("Trial Balance", 40, 30);
  const headers = [["Account", "Debit", "Credit"]];
  const tableRows = buildTrialBalanceRows(data);
  doc.autoTable({
    head: headers,
    body: tableRows,
    startY: 40,
    theme: "grid",
    headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] },
    bodyStyles: { fontSize: 8 },
  });
  doc.save("trial-balance.pdf");
};

const exportExcel = (data) => {
  const rows = [["Account", "Debit", "Credit"], ...buildTrialBalanceRows(data)];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Trial Balance");
  XLSX.writeFile(wb, "trial-balance.xlsx");
};

const handlePrint = () => {
  window.print();
};

const TrialBalance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getTrailBalance();
        setData(result);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to fetch trial balance");
        console.error("Error loading trial balance:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="tb-page">
        <Card className="tb-card">
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "50px" }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tb-page">
        <Card className="tb-card">
          <Alert message="Error" description={error} type="error" showIcon />
        </Card>
      </div>
    );
  }

  const debits = data?.debits || [];
  const credits = data?.credits || [];

  return (
    <div className="tb-page">
      <Card className="tb-card tb-card-modern" bordered={false}>
        <div className="tb-header">
          <h1 className="tb-main-title">Trial Balance</h1>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={() => exportPDF(data)}>
                  Download PDF
                </Menu.Item>
                <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={() => exportExcel(data)}>
                  Download Excel
                </Menu.Item>
                <Menu.Item key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
                  Print
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="small" className="tb-export-btn">
              Download / Print <DownOutlined />
            </Button>
          </Dropdown>
        </div>

        <div className="tb-table-wrap">
          <table className="tb-table">
            <thead>
              <tr>
                <th>ACCOUNT</th>
                <th className="tb-th-amount">DEBIT</th>
                <th className="tb-th-amount">CREDIT</th>
              </tr>
            </thead>
            <tbody>
              {debits.map((item, index) => (
                <tr key={`d-${(item.subAccount || "")}-${index}`}>
                  <td className="tb-account-name">{item.subAccount || "—"}</td>
                  <td className="tb-amount">{formatAmount(item.balance)}</td>
                  <td className="tb-amount"></td>
                </tr>
              ))}
              {credits.map((item, index) => (
                <tr key={`c-${(item.subAccount || "")}-${index}`}>
                  <td className="tb-account-name">{item.subAccount || "—"}</td>
                  <td className="tb-amount"></td>
                  <td className="tb-amount">{formatAmount(item.balance)}</td>
                </tr>
              ))}
              <tr className="tb-total-row">
                <td><strong>TOTAL</strong></td>
                <td className="tb-amount"><strong>{formatAmount(data?.totalDebit)}</strong></td>
                <td className="tb-amount"><strong>{formatAmount(data?.totalCredit)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="tb-footer">
          <span className="tb-summary">
            Total Debit: {formatAmount(data?.totalDebit)} &nbsp;|&nbsp; Total Credit: {formatAmount(data?.totalCredit)}
          </span>
        </div>
      </Card>
    </div>
  );
};

export default TrialBalance;
